/**
 * GoPro Labs command primitives.
 *
 * A Labs QR code is a plain-text string of concatenated tokens, e.g.
 *
 *     mVr5Xp30cFd1wN$DIVE=1$WBLK=1$EXPN=240$BITR=120
 *
 * Reference: https://gopro.github.io/labs/control/tech/
 *
 * We model a command as an ordered list of tokens so the app can reason about
 * each one — validate it, explain it, gate it on camera model — instead of
 * pushing an opaque string around.
 */

import { supports } from "./cameras";
import type { Camera, Feature } from "./cameras";

/** Labs parses a single QR payload up to ~255 characters. */
export const MAX_SINGLE_QR_CHARS = 255;

export type TokenKind =
  | "setting" // mV, r5X, p30, cF, wN
  | "option" // oV1, oB4
  | "ext_temp" // $FOUR=value, until power off
  | "ext_perm" // *FOUR=value, survives power off
  | "action" // !S, !60E, !R
  | "cond" // >06:00, <18:00
  | "message"; // "Dive on"

export interface Token {
  text: string;
  kind: TokenKind;
  /** Human name, e.g. "White balance". */
  label: string;
  /** Why *this* preset chose it. The explanation is the product as much as the
   *  code is — it is what makes someone trust a preset they cannot preview
   *  until they surface. */
  rationale: string;
  /** The capability this token needs, if it needs one. */
  feature?: Feature;
}

export function setting(
  text: string,
  label: string,
  rationale: string,
  feature?: Feature,
): Token {
  return { text, kind: "setting", label, rationale, feature };
}

export function extension(
  fourcc: string,
  value: string | number,
  label: string,
  rationale: string,
  feature: Feature,
  opts: { permanent?: boolean; quoted?: boolean } = {},
): Token {
  const rendered = opts.quoted ? `"${value}"` : String(value);
  return {
    text: `${opts.permanent ? "*" : "$"}${fourcc}=${rendered}`,
    kind: opts.permanent ? "ext_perm" : "ext_temp",
    label,
    rationale,
    feature,
  };
}

export function tokenSupported(token: Token, camera: Camera): boolean {
  return token.feature ? supports(camera, token.feature) : true;
}
